import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import {
  Factory, ClipboardList, TrendingUp, Plus, RefreshCw, Calendar, Package,
  ArrowUpRight, AlertTriangle, CheckCircle2,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Consolidated HO Production Planner (Super Admin only). Reads the same
 * data used by /app/mfg — this page is a single-glance overview + quick
 * plan creation across every factory unit.
 */
export default function HoPlannerPage() {
  const [factories, setFactories] = useState([]);
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0,10); });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [f, p, pr] = await Promise.all([
        api.get("/factories"),
        api.get(`/mfg/plans?from=${from}&to=${to}`).catch(() => api.get("/mfg/plans")),
        api.get("/products"),
      ]);
      setFactories(f.data || []);
      const raw = p.data;
      // Filter to date range client-side if backend didn't accept params
      const rows = (Array.isArray(raw) ? raw : []).filter(x => {
        const d = x.plan_date || "";
        return (!from || d >= from) && (!to || d <= to);
      });
      setPlans(rows);
      setProducts(pr.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load HO planner");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const kpis = useMemo(() => {
    const byStatus = { planned: 0, in_progress: 0, completed: 0, cancelled: 0 };
    let target = 0, actual = 0;
    plans.forEach(p => {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1;
      target += Number(p.target_qty) || 0;
      actual += Number(p.actual_qty) || 0;
    });
    const attainment = target > 0 ? Math.round((actual / target) * 100) : 0;
    return { total: plans.length, ...byStatus, target, actual, attainment };
  }, [plans]);

  const byFactory = useMemo(() => {
    const map = new Map();
    factories.forEach(f => map.set(f.id, { factory: f, plans: [], target: 0, actual: 0 }));
    plans.forEach(p => {
      const b = map.get(p.branch_id);
      if (b) { b.plans.push(p); b.target += Number(p.target_qty)||0; b.actual += Number(p.actual_qty)||0; }
    });
    return Array.from(map.values()).sort((a,b) => (b.target - a.target));
  }, [plans, factories]);

  return (
    <div className="space-y-6" data-testid="ho-planner-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">Head Office · Production Planner</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">HO Production Planner</h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Assign daily production targets to every factory unit. One glance shows plan-vs-actual across all sites.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ho-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-[#C05621] text-white hover:bg-[#a04519]" data-testid="ho-new-plan-btn">
            <Plus className="w-4 h-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap items-end gap-2 p-3 bg-white border border-[#EAE5D9] rounded-md">
        <div><Label className="text-[10px] uppercase text-[#8B7355]">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ho-from" /></div>
        <div><Label className="text-[10px] uppercase text-[#8B7355]">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ho-to" /></div>
        <Button type="submit" size="sm" className="bg-[#2C1810] text-white">Apply</Button>
      </form>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total Plans" value={kpis.total} tone="neutral" icon={ClipboardList} />
        <Kpi label="Planned" value={kpis.planned} tone="amber" icon={Calendar} />
        <Kpi label="In Progress" value={kpis.in_progress} tone="blue" icon={Factory} />
        <Kpi label="Completed" value={kpis.completed} tone="emerald" icon={CheckCircle2} />
        <Kpi label="Attainment" value={`${kpis.attainment}%`} tone={kpis.attainment >= 80 ? "emerald" : kpis.attainment >= 50 ? "amber" : "rose"} icon={TrendingUp} />
      </div>

      {/* Per-factory grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {byFactory.map(({ factory, plans: fplans, target, actual }) => {
          const att = target > 0 ? Math.round((actual/target)*100) : 0;
          return (
            <div key={factory.id} className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden" data-testid={`ho-factory-${factory.id}`}>
              <div className="p-3 border-b border-[#EAE5D9] bg-gradient-to-br from-[#FEF9EA] to-white flex items-center justify-between">
                <div className="min-w-0">
                  <div className="uc-label !text-[#8B7355]">{factory.code}</div>
                  <div className="font-[Outfit] font-semibold truncate">{factory.name}</div>
                </div>
                <Link to="/app/mfg" className="text-[#C05621] text-xs hover:underline flex items-center">
                  Open MES <ArrowUpRight className="w-3 h-3 ml-0.5" />
                </Link>
              </div>
              <div className="p-3 text-xs space-y-1.5">
                <div className="flex justify-between"><span className="text-[#8B7355]">Plans</span><b>{fplans.length}</b></div>
                <div className="flex justify-between"><span className="text-[#8B7355]">Target</span><b>{target.toLocaleString()}</b></div>
                <div className="flex justify-between"><span className="text-[#8B7355]">Actual</span><b className="text-emerald-700">{actual.toLocaleString()}</b></div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-[#EAE5D9] rounded overflow-hidden">
                    <div className={`h-full ${att>=80?'bg-emerald-500':att>=50?'bg-amber-500':'bg-rose-500'}`} style={{ width: `${Math.min(100,att)}%` }} />
                  </div>
                  <span className="text-[10px] font-mono">{att}%</span>
                </div>
                {fplans.filter(p => p.status === "in_progress").length > 0 && (
                  <Badge className="border-0 bg-blue-500 text-white text-[10px]">
                    {fplans.filter(p => p.status === "in_progress").length} live
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
        {factories.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-[#EAE5D9] rounded-lg">
            <Factory className="w-10 h-10 mx-auto text-[#8B7355] opacity-40 mb-2" />
            <div className="text-sm text-[#8B7355]">
              No factories yet. Create one at <Link to="/app/factories" className="text-[#C05621] underline">Factories</Link>.
            </div>
          </div>
        )}
      </div>

      {/* Plans table */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
        <div className="p-3 border-b border-[#EAE5D9] flex items-center gap-2 bg-[#FEF9EA]">
          <ClipboardList className="w-4 h-4 text-[#C05621]" />
          <div className="font-[Outfit] font-semibold text-sm">Recent plans ({plans.length})</div>
        </div>
        <div className="overflow-x-auto max-h-[480px]" data-testid="ho-plans-table">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-[#8B7355] border-b sticky top-0 bg-white">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Factory</th>
                <th className="text-left p-2">Product</th>
                <th className="text-right p-2">Target</th>
                <th className="text-right p-2">Actual</th>
                <th className="text-right p-2">%</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {plans.map(p => {
                const fact = factories.find(f => f.id === p.branch_id);
                const prod = products.find(x => x.id === p.product_id);
                const tone = p.status === "completed" ? "emerald" : p.status === "in_progress" ? "blue" : p.status === "cancelled" ? "rose" : "amber";
                return (
                  <tr key={p.id} data-testid={`ho-plan-row-${p.id}`}>
                    <td className="p-2 whitespace-nowrap">{p.plan_date}</td>
                    <td className="p-2">{fact?.name || p.branch_id.slice(0,8)}</td>
                    <td className="p-2">{prod?.name || p.product_id.slice(0,8)}</td>
                    <td className="p-2 text-right">{p.target_qty}</td>
                    <td className="p-2 text-right">{p.actual_qty || 0}</td>
                    <td className="p-2 text-right">{p.completion_pct || 0}%</td>
                    <td className="p-2">
                      <Badge className={`border-0 bg-${tone}-500 text-white text-[10px] capitalize`}>{p.status.replace("_"," ")}</Badge>
                    </td>
                  </tr>
                );
              })}
              {plans.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#8B7355]">No plans in range</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <NewPlanDialog
          factories={factories}
          products={products}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone, icon: Icon }) {
  const bg = ({emerald:"bg-emerald-50 border-emerald-200 text-emerald-900",blue:"bg-blue-50 border-blue-200 text-blue-900",amber:"bg-amber-50 border-amber-200 text-amber-900",rose:"bg-rose-50 border-rose-200 text-rose-900",neutral:"bg-white border-[#EAE5D9]"})[tone] || "bg-white";
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function NewPlanDialog({ factories, products, onClose, onCreated }) {
  const [form, setForm] = useState({
    plan_date: today(),
    branch_id: factories[0]?.id || "",
    product_id: products[0]?.id || "",
    target_qty: 100,
    priority: "medium",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.branch_id || !form.product_id) { toast.error("Pick a factory + product"); return; }
    setBusy(true);
    try {
      await api.post("/mfg/plans", { ...form, target_qty: Number(form.target_qty) || 0 });
      toast.success("Plan created");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create plan"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ho-new-plan-dialog">
      <div className="bg-white rounded-lg border border-[#EAE5D9] w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-[Outfit] text-lg font-semibold">Create Manufacturing Plan</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={form.plan_date} onChange={(e) => setForm({...form, plan_date: e.target.value})} data-testid="ho-plan-date" /></div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
              <SelectTrigger data-testid="ho-plan-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["low","medium","high","urgent"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Factory Unit</Label>
            <Select value={form.branch_id} onValueChange={(v) => setForm({...form, branch_id: v})}>
              <SelectTrigger data-testid="ho-plan-branch"><SelectValue placeholder="Pick a factory" /></SelectTrigger>
              <SelectContent className="bg-white">
                {factories.map(f => <SelectItem key={f.id} value={f.id}>{f.name} · {f.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>SKU / Product</Label>
            <Select value={form.product_id} onValueChange={(v) => setForm({...form, product_id: v})}>
              <SelectTrigger data-testid="ho-plan-product"><SelectValue placeholder="Pick a product" /></SelectTrigger>
              <SelectContent className="bg-white">
                {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.sku}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Target Qty</Label><Input type="number" min="1" value={form.target_qty} onChange={(e) => setForm({...form, target_qty: e.target.value})} data-testid="ho-plan-qty" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="ho-plan-notes" /></div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#C05621] text-white" data-testid="ho-plan-save"><Plus className="w-3.5 h-3.5 mr-1" /> Create</Button>
        </div>
      </div>
    </div>
  );
}
